import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Radio, ChevronRight, ChevronLeft, Check, Film, Trophy, Swords, Users, UserRound,
  Gamepad2, MessageCircle, MapPin, Clock, Image as ImageIcon, ShieldQuestion,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePrefs, STATES, TIMEZONES } from "../context/PrefsContext";

const FACTIONS = [
  { id: "LONESTAR", color: "#3498DB", logo: "/factions/lonestar.webp", blurb: "Precision & recon specialists." },
  { id: "VALKYRA", color: "#E4443A", logo: "/factions/valkyra.webp", blurb: "Aggressive front-line assault." },
  { id: "MANTICORE", color: "#2ECC71", logo: "/factions/manticore.webp", blurb: "Support, armour & logistics." },
];

const TOUR = [
  { icon: Film, title: "Clips", desc: "Share your best frags — top clips get posted to our socials." },
  { icon: Trophy, title: "Season Pass", desc: "Earn XP, climb tiers and unlock loot every season." },
  { icon: Swords, title: "Ladders", desc: "Compete in ranked ladders and clan challenges." },
  { icon: Users, title: "Clans", desc: "Squad up, rep a tag and fight clan-vs-clan." },
  { icon: UserRound, title: "Your Profile", desc: "Track kills, K/D, rank and show off your dog-tag." },
];

const STEPS = ["welcome", "tour", "faction", "identity", "avatar", "connect", "locale", "done"];

const WelcomeWizard = () => {
  const { user, ready, setUser } = useAuth();
  const { prefs, update: updatePrefs } = usePrefs();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const firedRef = useRef(false);

  // form state
  const [faction, setFaction] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarSource, setAvatarSource] = useState("custom");
  const [steamId, setSteamId] = useState("");
  const [discord, setDiscord] = useState("");
  const [region, setRegion] = useState("");
  const [tz, setTz] = useState("auto");
  const timerRef = useRef(null);
  const CONNECT_STEP = STEPS.indexOf("connect");

  const prefill = (u) => {
    setFaction(u.faction || "");
    setDisplayName(u.display_name || u.callsign || "");
    setBio(u.bio || "");
    setAvatarUrl(u.avatar_url || "");
    setAvatarSource(u.avatar_source || "custom");
    setSteamId(u.steam_id || "");
    setDiscord(u.discord_id || "");
    setRegion(prefs.state_flag || "");
    setTz(prefs.timezone || "auto");
  };

  const linkSteam = () => {
    try { localStorage.setItem("wardogs_welcome_resume", String(CONNECT_STEP)); } catch (e) { /* ignore */ }
    window.location.href = `${API}/auth/steam/start?mode=link&user_id=${user.id}`;
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // open on demand (Profile "Replay setup" / progress nudge)
  useEffect(() => {
    const handler = (e) => {
      if (!user || user === false) return;
      prefill(user);
      setStep((e.detail && e.detail.step) || 0);
      setOpen(true);
    };
    window.addEventListener("open-welcome-wizard", handler);
    return () => window.removeEventListener("open-welcome-wizard", handler);
  }, [user, prefs.state_flag, prefs.timezone]); // eslint-disable-line

  useEffect(() => {
    if (!ready || firedRef.current) return;
    if (!user || user === false) return;
    if (user.onboarding_completed === true) return;
    if (localStorage.getItem("wardogs_welcome_done")) return;
    firedRef.current = true;
    prefill(user);
    let startStep = 0;
    try {
      const r = localStorage.getItem("wardogs_welcome_resume");
      if (r) { startStep = parseInt(r, 10) || 0; localStorage.removeItem("wardogs_welcome_resume"); }
    } catch (e) { /* ignore */ }
    setStep(startStep);
    timerRef.current = setTimeout(() => setOpen(true), startStep ? 200 : 1200);
  }, [ready, user]); // eslint-disable-line

  const stepKey = STEPS[step];
  const total = STEPS.length;

  const patch = async (payload) => {
    try {
      const { data } = await api.patch("/auth/link", payload);
      if (setUser) setUser(data);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save — try again");
      return false;
    }
  };

  const finishOnboarding = async () => {
    localStorage.setItem("wardogs_welcome_done", "1");
    setOpen(false);
    try {
      const { data } = await api.patch("/auth/link", { onboarding_completed: true });
      if (setUser) setUser(data);
    } catch (e) { /* non-blocking */ }
  };

  const next = () => setStep((s) => Math.min(s + 1, total - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const saveFaction = async () => {
    if (!faction) { next(); return; }
    setBusy(true);
    const ok = await patch({ faction });
    setBusy(false);
    if (ok) next();
  };
  const saveIdentity = async () => {
    setBusy(true);
    await patch({ display_name: displayName.trim(), bio: bio.trim() });
    setBusy(false);
    next();
  };
  const saveAvatar = async () => {
    setBusy(true);
    await patch({ avatar_url: avatarUrl.trim(), avatar_source: avatarSource });
    setBusy(false);
    next();
  };
  const saveConnect = async () => {
    setBusy(true);
    await patch({ steam_id: steamId.trim(), discord_id: discord.trim() });
    setBusy(false);
    next();
  };
  const saveLocale = async () => {
    setBusy(true);
    try { await updatePrefs({ state_flag: region || null, timezone: tz }); } catch (e) { /* non-blocking */ }
    setBusy(false);
    next();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) finishOnboarding(); }}>
      <DialogContent
        data-testid={`welcome-wizard-${stepKey}`}
        className="max-w-lg bg-[#13151B] border-[#D4A94A]/40 tactical-frame text-[#F1F5F9] p-0 overflow-hidden"
      >
        <div className="h-1.5 w-full bg-gradient-to-r from-[#D4A94A] via-[#E5BD60] to-[#D4A94A]" />
        <div className="p-6">
          {/* progress dots */}
          <div className="flex items-center gap-1.5 mb-5" data-testid="welcome-progress">
            {STEPS.map((_, i) => (
              <span key={i} className="h-1 flex-1 rounded-full transition-colors"
                style={{ background: i <= step ? "#D4A94A" : "#2A2E3D" }} />
            ))}
          </div>

          {stepKey === "welcome" && (
            <div data-testid="welcome-step-welcome">
              <div className="flex items-center gap-2 mb-1"><Radio className="w-4 h-4 text-[#D4A94A]" /><span className="overline text-[#D4A94A]">Welcome, operator</span></div>
              <DialogTitle className="font-heading font-black uppercase text-3xl leading-tight mb-3">
                G'day {user?.callsign || "soldier"} — welcome to OCE DOGS
              </DialogTitle>
              <p className="font-mono text-xs text-[#94A3B8] leading-relaxed mb-5">
                You've enlisted in the Oceanic WARDOGS community HQ. Let's take 60 seconds to show you around and get your dossier set up. You can skip any step and change everything later on your Profile.
              </p>
              <img src="/oce-dogs-logo.webp" alt="OCE DOGS" className="h-16 w-auto mx-auto my-4 opacity-90" />
            </div>
          )}

          {stepKey === "tour" && (
            <div data-testid="welcome-step-tour">
              <DialogTitle className="font-heading font-black uppercase text-2xl leading-tight mb-1">What you can do here</DialogTitle>
              <p className="font-mono text-xs text-[#94A3B8] mb-4">The essentials — all free for members.</p>
              <div className="space-y-2.5">
                {TOUR.map((it) => (
                  <div key={it.title} className="flex items-start gap-3 bg-[#0B0C0E] border border-[#2A2E3D] tactical-frame-sm p-3" data-testid={`welcome-tour-${it.title.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="w-9 h-9 flex items-center justify-center border border-[#D4A94A]/40 text-[#D4A94A] shrink-0"><it.icon className="w-4 h-4" /></div>
                    <div>
                      <div className="font-stencil font-black uppercase text-sm text-[#F1F5F9]">{it.title}</div>
                      <div className="font-mono text-[11px] text-[#94A3B8] leading-snug">{it.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stepKey === "faction" && (
            <div data-testid="welcome-step-faction">
              <DialogTitle className="font-heading font-black uppercase text-2xl leading-tight mb-1">Choose your faction</DialogTitle>
              <p className="font-mono text-xs text-[#94A3B8] mb-4">Pick an allegiance. <span className="text-[#64748B]">Heads up — faction is locked for the week once set.</span></p>
              <div className="grid grid-cols-3 gap-2">
                {FACTIONS.map((f) => (
                  <button key={f.id} data-testid={`welcome-faction-${f.id.toLowerCase()}`} onClick={() => setFaction(f.id)}
                    className="flex flex-col items-center gap-2 p-3 tactical-frame-sm transition-all"
                    style={{ borderColor: faction === f.id ? f.color : "#2A2E3D", borderWidth: 1, borderStyle: "solid", background: faction === f.id ? f.color + "1A" : "#0B0C0E" }}>
                    <img src={f.logo} alt={f.id} className="w-10 h-10 object-contain" />
                    <span className="font-stencil font-black uppercase text-xs" style={{ color: f.color }}>{f.id}</span>
                    <span className="font-mono text-[10px] text-[#94A3B8] text-center leading-tight">{f.blurb}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {stepKey === "identity" && (
            <div data-testid="welcome-step-identity">
              <DialogTitle className="font-heading font-black uppercase text-2xl leading-tight mb-1">Your identity</DialogTitle>
              <p className="font-mono text-xs text-[#94A3B8] mb-4">How you'll show up across the community.</p>
              <label className="overline block mb-1.5">Display name</label>
              <input data-testid="welcome-display-name" value={displayName} maxLength={15} onChange={(e) => setDisplayName(e.target.value)}
                className="w-full bg-[#0B0C0E] border border-[#2A2E3D] focus:border-[#D4A94A] outline-none text-sm font-mono px-3 py-2.5 mb-4 transition-colors" placeholder="Callsign shown publicly" />
              <label className="overline block mb-1.5">Short bio</label>
              <textarea data-testid="welcome-bio" value={bio} maxLength={280} onChange={(e) => setBio(e.target.value)} rows={3}
                className="w-full bg-[#0B0C0E] border border-[#2A2E3D] focus:border-[#D4A94A] outline-none text-sm font-mono px-3 py-2.5 transition-colors resize-none" placeholder="Tell the squad about yourself (optional)" />
              <div className="text-right font-mono text-[10px] text-[#64748B] mt-1">{bio.length}/280</div>
            </div>
          )}

          {stepKey === "avatar" && (
            <div data-testid="welcome-step-avatar">
              <DialogTitle className="font-heading font-black uppercase text-2xl leading-tight mb-1">Pick an avatar</DialogTitle>
              <p className="font-mono text-xs text-[#94A3B8] mb-4">Choose where your profile picture comes from.</p>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 border border-[#2A2E3D] bg-[#0B0C0E] flex items-center justify-center overflow-hidden shrink-0">
                  {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-[#64748B]" />}
                </div>
                <div className="flex flex-col gap-1.5">
                  {["custom", user?.steam_id && "steam", user?.discord_id && "discord"].filter(Boolean).map((src) => (
                    <button key={src} data-testid={`welcome-avatar-src-${src}`} onClick={() => setAvatarSource(src)}
                      className="text-left px-3 py-1.5 tactical-frame-sm font-mono text-xs transition-colors"
                      style={{ borderColor: avatarSource === src ? "#D4A94A" : "#2A2E3D", borderWidth: 1, borderStyle: "solid", color: avatarSource === src ? "#D4A94A" : "#94A3B8" }}>
                      {src === "custom" ? "Custom image URL" : src === "steam" ? "Use Steam avatar" : "Use Discord avatar"}
                    </button>
                  ))}
                </div>
              </div>
              {avatarSource === "custom" && (
                <>
                  <label className="overline block mb-1.5">Image URL</label>
                  <input data-testid="welcome-avatar-url" value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)}
                    className="w-full bg-[#0B0C0E] border border-[#2A2E3D] focus:border-[#D4A94A] outline-none text-sm font-mono px-3 py-2.5 transition-colors" placeholder="https://…" />
                </>
              )}
              <p className="font-mono text-[10px] text-[#64748B] mt-3">Link Steam or Discord on the next step to use those avatars.</p>
            </div>
          )}

          {stepKey === "connect" && (
            <div data-testid="welcome-step-connect">
              <DialogTitle className="font-heading font-black uppercase text-2xl leading-tight mb-1">Link your accounts</DialogTitle>
              <p className="font-mono text-xs text-[#94A3B8] mb-4">Your Steam ID lets us track your in-game kills, wins and XP. Discord is for community pings.</p>
              {user?.steam_id ? (
                <div data-testid="welcome-steam-linked" className="flex items-center gap-2 mb-4 bg-[#1b2838]/40 border border-[#2ECC71]/40 tactical-frame-sm px-3 py-2.5 font-mono text-xs text-[#2ECC71]"><Check className="w-4 h-4" /> Steam linked</div>
              ) : (
                <button data-testid="welcome-steam-oneclick" onClick={linkSteam} className="w-full inline-flex items-center justify-center gap-2 bg-[#1b2838] hover:bg-[#2a3f5f] text-white font-stencil font-black uppercase text-sm px-4 py-3 tactical-frame-sm transition-colors mb-3"><Gamepad2 className="w-4 h-4" /> Sign in with Steam</button>
              )}
              <div className="overline text-center text-[#64748B] mb-3">or enter manually</div>
              <label className="overline flex items-center gap-1.5 mb-1.5"><Gamepad2 className="w-3.5 h-3.5" /> Steam ID (17-digit)</label>
              <input data-testid="welcome-steam-id" value={steamId} onChange={(e) => setSteamId(e.target.value)}
                className="w-full bg-[#0B0C0E] border border-[#2A2E3D] focus:border-[#D4A94A] outline-none text-sm font-mono px-3 py-2.5 mb-4 transition-colors" placeholder="7656119…" />
              <label className="overline flex items-center gap-1.5 mb-1.5"><MessageCircle className="w-3.5 h-3.5" /> Discord handle</label>
              <input data-testid="welcome-discord" value={discord} onChange={(e) => setDiscord(e.target.value)}
                className="w-full bg-[#0B0C0E] border border-[#2A2E3D] focus:border-[#D4A94A] outline-none text-sm font-mono px-3 py-2.5 transition-colors" placeholder="yourname" />
              <p className="font-mono text-[10px] text-[#64748B] mt-3">Prefer one-click? You can sign in with Steam from your Profile anytime.</p>
            </div>
          )}

          {stepKey === "locale" && (
            <div data-testid="welcome-step-locale">
              <DialogTitle className="font-heading font-black uppercase text-2xl leading-tight mb-1">Region & time</DialogTitle>
              <p className="font-mono text-xs text-[#94A3B8] mb-4">So match times and leaderboards line up with your local clock.</p>
              <label className="overline flex items-center gap-1.5 mb-1.5"><MapPin className="w-3.5 h-3.5" /> Region</label>
              <select data-testid="welcome-region" value={region} onChange={(e) => setRegion(e.target.value)}
                className="w-full bg-[#0B0C0E] border border-[#2A2E3D] focus:border-[#D4A94A] outline-none text-sm font-mono px-3 py-2.5 mb-4 transition-colors">
                <option value="">Not set</option>
                {STATES.map((s) => <option key={s.id} value={s.id}>{s.country} · {s.label}</option>)}
              </select>
              <label className="overline flex items-center gap-1.5 mb-1.5"><Clock className="w-3.5 h-3.5" /> Timezone</label>
              <select data-testid="welcome-timezone" value={tz} onChange={(e) => setTz(e.target.value)}
                className="w-full bg-[#0B0C0E] border border-[#2A2E3D] focus:border-[#D4A94A] outline-none text-sm font-mono px-3 py-2.5 transition-colors">
                {TIMEZONES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          )}

          {stepKey === "done" && (
            <div data-testid="welcome-step-done" className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#2ECC71]/15 border border-[#2ECC71]/50 flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7 text-[#2ECC71]" /></div>
              <DialogTitle className="font-heading font-black uppercase text-2xl leading-tight mb-2">You're locked and loaded</DialogTitle>
              <p className="font-mono text-xs text-[#94A3B8] leading-relaxed">
                Your dossier is set up. Jump into the server, share a clip, or climb the ladder. You can tweak anything on your Profile whenever you like.
              </p>
            </div>
          )}

          {/* nav */}
          <div className="flex items-center gap-3 mt-6">
            {step > 0 && stepKey !== "done" && (
              <button data-testid="welcome-back" onClick={back} className="inline-flex items-center gap-1 font-mono text-xs text-[#94A3B8] hover:text-[#F1F5F9] transition-colors"><ChevronLeft className="w-4 h-4" /> Back</button>
            )}
            <div className="ml-auto flex items-center gap-3">
              {stepKey !== "welcome" && stepKey !== "tour" && stepKey !== "done" && (
                <button data-testid="welcome-skip" onClick={next} className="font-mono text-xs text-[#64748B] hover:text-[#94A3B8] transition-colors">Skip</button>
              )}
              {stepKey === "welcome" && (
                <button data-testid="welcome-start" onClick={next} className="inline-flex items-center justify-center gap-2 bg-[#D4A94A] hover:bg-[#E5BD60] text-[#0B0C0E] font-stencil font-black uppercase text-sm px-5 py-2.5 tactical-frame-sm transition-colors">Take the tour <ChevronRight className="w-4 h-4" /></button>
              )}
              {stepKey === "tour" && (
                <button data-testid="welcome-tour-next" onClick={next} className="inline-flex items-center justify-center gap-2 bg-[#D4A94A] hover:bg-[#E5BD60] text-[#0B0C0E] font-stencil font-black uppercase text-sm px-5 py-2.5 tactical-frame-sm transition-colors">Set up my account <ChevronRight className="w-4 h-4" /></button>
              )}
              {stepKey === "faction" && (
                <button data-testid="welcome-faction-next" disabled={busy} onClick={saveFaction} className="inline-flex items-center justify-center gap-2 bg-[#D4A94A] hover:bg-[#E5BD60] disabled:opacity-60 text-[#0B0C0E] font-stencil font-black uppercase text-sm px-5 py-2.5 tactical-frame-sm transition-colors">Continue <ChevronRight className="w-4 h-4" /></button>
              )}
              {stepKey === "identity" && (
                <button data-testid="welcome-identity-next" disabled={busy} onClick={saveIdentity} className="inline-flex items-center justify-center gap-2 bg-[#D4A94A] hover:bg-[#E5BD60] disabled:opacity-60 text-[#0B0C0E] font-stencil font-black uppercase text-sm px-5 py-2.5 tactical-frame-sm transition-colors">Continue <ChevronRight className="w-4 h-4" /></button>
              )}
              {stepKey === "avatar" && (
                <button data-testid="welcome-avatar-next" disabled={busy} onClick={saveAvatar} className="inline-flex items-center justify-center gap-2 bg-[#D4A94A] hover:bg-[#E5BD60] disabled:opacity-60 text-[#0B0C0E] font-stencil font-black uppercase text-sm px-5 py-2.5 tactical-frame-sm transition-colors">Continue <ChevronRight className="w-4 h-4" /></button>
              )}
              {stepKey === "connect" && (
                <button data-testid="welcome-connect-next" disabled={busy} onClick={saveConnect} className="inline-flex items-center justify-center gap-2 bg-[#D4A94A] hover:bg-[#E5BD60] disabled:opacity-60 text-[#0B0C0E] font-stencil font-black uppercase text-sm px-5 py-2.5 tactical-frame-sm transition-colors">Continue <ChevronRight className="w-4 h-4" /></button>
              )}
              {stepKey === "locale" && (
                <button data-testid="welcome-locale-next" disabled={busy} onClick={saveLocale} className="inline-flex items-center justify-center gap-2 bg-[#D4A94A] hover:bg-[#E5BD60] disabled:opacity-60 text-[#0B0C0E] font-stencil font-black uppercase text-sm px-5 py-2.5 tactical-frame-sm transition-colors">Continue <ChevronRight className="w-4 h-4" /></button>
              )}
              {stepKey === "done" && (
                <button data-testid="welcome-finish" onClick={finishOnboarding} className="inline-flex items-center justify-center gap-2 bg-[#2ECC71] hover:bg-[#2ECC71]/80 text-[#0B0C0E] font-stencil font-black uppercase text-sm px-6 py-2.5 tactical-frame-sm transition-colors">Enter HQ <Check className="w-4 h-4" /></button>
              )}
            </div>
          </div>
          {stepKey !== "done" && (
            <button data-testid="welcome-dismiss" onClick={finishOnboarding} className="w-full text-center font-mono text-[10px] text-[#64748B] hover:text-[#94A3B8] mt-4 transition-colors inline-flex items-center justify-center gap-1"><ShieldQuestion className="w-3 h-3" /> Skip setup — I'll do it later</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeWizard;
